import { useEffect, useRef } from 'react';
import { useFakeAccess } from './useFakeAccess';
import { useSessionContext } from '@/features/auth';
import { useDefaultCompanyId } from './useImpersonationScope';
import { useOptionsQuery } from '@/legacy/hooks';
import { COMPANIES_OPTIONS } from '@/legacy/constants';
import urls from '@/legacy/config/env';
import { useUserInfo } from '@/features/space-user';

export const useAutoImpersonate = (): {
	isPending: boolean;
	isError: boolean;
	error: unknown;
} => {
	const { isSuperUser } = useSessionContext();
	const { data: userInfo } = useUserInfo();
	const currentCompanyId = userInfo?.companyId;
	const defaultCompanyId = useDefaultCompanyId();
	const hasFiredRef = useRef(false);

	const { loginFakeAccess, isPending, isError, error } = useFakeAccess({});

	const { data: companies, isLoading: isLoadingCompanies } = useOptionsQuery(
		COMPANIES_OPTIONS,
		urls.formOptions.companies,
	);

	const companyName = companies?.find((c) => c.value === defaultCompanyId)?.label ?? '';

	useEffect(() => {
		if (hasFiredRef.current) return;
		if (!isSuperUser) return;
		if (currentCompanyId) return;
		if (!defaultCompanyId) return;
		if (!companyName) return;
		if (isLoadingCompanies) return;
		if (isPending) return;

		hasFiredRef.current = true;
		loginFakeAccess({
			companyId: defaultCompanyId,
			companyName,
			navigateOnSuccess: false,
		});
	}, [
		isSuperUser,
		currentCompanyId,
		defaultCompanyId,
		companyName,
		isLoadingCompanies,
		isPending,
		loginFakeAccess,
	]);

	return { isPending: isPending || isLoadingCompanies, isError, error };
};
